import { useNavigate } from "@tanstack/react-router";
import { useCallback } from "react";
import { useMedia, useTitle } from "react-use";

import { extendResult } from "@blis/shared/models/result";

import { Centered } from "@/components/ui/container";
import {
  Dialog,
  DialogBody,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Link } from "@/components/ui/link";
import { Sheet } from "@/components/ui/sheet";
import { Text } from "@/components/ui/text";
import { useAuth } from "@/features/auth/hooks";
import type { LocationState } from "@/lib/types";
import { useTinyKeys } from "@/lib/useTinyKeys";
import { worklistNameWithCodes } from "../utils";
import { ResultForm } from "./ResultForm";
import { useWorklist } from "./WorklistProvider";

export function ResultSidebar() {
  const { order, orders, worklist } = useWorklist();
  useTitle(`Results - ${worklistNameWithCodes(worklist)}`);
  const isWide = useMedia("(min-width: 1280px)");
  const navigate = useNavigate();
  const state = history.state as LocationState;
  const { user } = useAuth();

  useTinyKeys(
    {
      ArrowLeft: () => {
        const el = document.activeElement;
        el && (el as HTMLElement).blur?.();
      },
    },
    [],
    false,
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: state is needed for the replace
  const onLastResultComplete = useCallback(() => {
    const currentIndex = orders.findIndex((o) => o.id === order?.id);
    const nextOrder = currentIndex >= 0 ? orders[currentIndex + 1] : null;
    if (nextOrder) {
      navigate({
        from: "/runs/$id/worklist/$testId/results/$orderId",
        to: "/runs/$id/worklist/$testId/results/$orderId",
        params: {
          orderId: nextOrder.id,
          testId: nextOrder.testId,
          id: nextOrder.testRunId ?? -1,
        },
        replace: true,
        state,
      });
    }
  }, [navigate, order?.id, orders, state]);

  if (!order) {
    return <Centered>Test order not found.</Centered>;
  }
  const requiresEntry = order.results
    .map(extendResult)
    .filter(
      (r) =>
        !r.$initial || (!r.validated && r.enteredById !== user?.profile.id),
    );

  const form = (
    <ResultForm
      order={order}
      startValue={
        requiresEntry[0]?.id.toString() || order.results[0]?.id.toString()
      }
      onLastResultComplete={onLastResultComplete}
      className="w-full lg:w-[400px] p-5"
      autoFocus={requiresEntry.length > 0}
      header={
        <>
          <Text bold>Enter results</Text>
          <div className="flex gap-1 pb-3">
            <Text>Accession:</Text>
            <Link
              href={{
                to: "/accessions/$id",
                params: { id: order.accessionId },
                state: {
                  href: { to: "/runs" },
                },
              }}
              className="text-sm font-medium text-muted-foreground"
            >
              {order.accessionId}
            </Link>
          </div>
        </>
      }
    />
  );

  if (!isWide) {
    return (
      <Sheet
        isOpen={true}
        onOpenChange={(flag) => !flag && history.back()}
        classNames={{
          // full width on mobile
          content: "left-2 w-auto sm:left-auto sm:right-2 sm:max-w-2xl",
        }}
      >
        <Dialog>
          <DialogHeader>
            <DialogTitle>Enter result</DialogTitle>
          </DialogHeader>
          <DialogBody className="sm:px-0">{order && form}</DialogBody>
        </Dialog>
      </Sheet>
    );
  }

  return <div className="overflow-y-auto">{form}</div>;
}
